import React, { useState } from 'react';
import GenderInput from './GenderInput';
import Chatbot from './Chatbot';
import { calculateCompatibility } from '../services/api';
import { localizeChineseText } from '../services/chineseText';

const emptyPerson = { birth_date: '', birth_time: '', is_male: true };
const personLabels = { a: 'compatPersonA', b: 'compatPersonB' };

const summaryDate = (person) => (
  person.birth_date && person.birth_time ? `${person.birth_date} ${person.birth_time}` : ''
);

const parsePerson = (person) => {
  const rawDate = String(person.birth_date || '').trim();
  const isoDateMatch = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const legacyDateMatch = rawDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const timeMatch = String(person.birth_time || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if ((!isoDateMatch && !legacyDateMatch) || !timeMatch) return null;
  const year = isoDateMatch ? Number(isoDateMatch[1]) : Number(legacyDateMatch[3]);
  const month = isoDateMatch ? Number(isoDateMatch[2]) : Number(legacyDateMatch[2]);
  const day = isoDateMatch ? Number(isoDateMatch[3]) : Number(legacyDateMatch[1]);
  const hour = Number(timeMatch[1]);
  const minute = Number(timeMatch[2]);
  const date = new Date(year, month - 1, day);
  const validDate = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  const validTime = hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59;
  if (!validDate || !validTime) return null;
  return { year, month, day, hour, minute, is_male: person.is_male };
};

const CompatibilityPage = ({ t, lang = 'zh-TW' }) => {
  const [people, setPeople] = useState({ a: { ...emptyPerson }, b: { ...emptyPerson, is_male: false } });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const txt = (value) => localizeChineseText(value || '', lang);

  const updatePerson = (key, changes) => {
    setPeople((current) => ({ ...current, [key]: { ...current[key], ...changes } }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const personA = parsePerson(people.a);
    const personB = parsePerson(people.b);
    if (!personA || !personB) {
      setError(t('compatValidationError'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const computed = await calculateCompatibility({
        person_a: { ...personA, lang },
        person_b: { ...personB, lang },
        lang
      });
      setResult(computed);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(err);
      setError(t('compatCalcError'));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPeople({ a: { ...emptyPerson }, b: { ...emptyPerson, is_male: false } });
    setResult(null);
    setError('');
  };

  const form = (
    <form className="bz-form compat-form" onSubmit={handleSubmit}>
      {(['a', 'b']).map((key) => (
        <section className="fc-card compat-person-form" key={key}>
          <h2 className="compat-person-form__title">{t(personLabels[key])}</h2>
          <fieldset className="bz-fieldset">
            <legend className="bz-fieldset__legend">{t('birthDateTime')}</legend>
            <div className="compat-date-grid">
              <div className="fc-field">
                <label className="fc-label" htmlFor={`compat-${key}-birth-date`}>{t('compatBirthDateLabel')}</label>
                <input
                  id={`compat-${key}-birth-date`}
                  className="fc-input"
                  type="date"
                  min="1900-01-01"
                  max="2100-12-31"
                  value={people[key].birth_date}
                  onChange={(event) => updatePerson(key, { birth_date: event.target.value })}
                />
              </div>
              <div className="fc-field">
                <label className="fc-label" htmlFor={`compat-${key}-birth-time`}>{t('compatBirthTimeLabel')}</label>
                <input
                  id={`compat-${key}-birth-time`}
                  className="fc-input"
                  type="time"
                  step="60"
                  value={people[key].birth_time}
                  onChange={(event) => updatePerson(key, { birth_time: event.target.value })}
                  placeholder={t('compatBirthTimePlaceholder')}
                />
              </div>
            </div>
          </fieldset>
          <GenderInput
            value={people[key].is_male}
            onChange={(value) => updatePerson(key, { is_male: value })}
            t={t}
          />
        </section>
      ))}
      {error && <div className="fc-error" role="alert">{error}</div>}
      <div className="bz-form__actions">
        <button type="submit" className="fc-btn fc-btn--primary fc-btn--lg" disabled={loading}>
          {loading ? t('calculating') : t('compatCalculate')}
        </button>
        <button type="button" className="fc-btn fc-btn--ghost" onClick={reset}>{t('reset')}</button>
      </div>
    </form>
  );

  const compatibility = result?.compatibility;
  const pairFortunes = result?.pair_fortunes;
  const personCards = compatibility?.persons ? [
    ['a', compatibility.persons.a],
    ['b', compatibility.persons.b]
  ] : [];
  const pairFortuneCards = pairFortunes?.persons ? [
    ['a', pairFortunes.persons.a],
    ['b', pairFortunes.persons.b]
  ] : [];

  return (
    <div className="bz-compat-page">
      <section className="bz-intro bz-intro--compact fc-rise">
        <a className="fc-btn fc-btn--ghost fc-btn--sm" href="/bazi/">{t('backToIntegrated')}</a>
        <h1 className="bz-intro__title">{t('compatPageTitle')}</h1>
        <p className="bz-intro__desc">{t('compatPageDesc')}</p>
      </section>

      {!result && <div className="bz-form-card compat-form-card">{form}</div>}

      {result && compatibility && (
        <section className="bz-result compat-result">
          <div className="bz-recap">
            <div className="bz-recap__info">
              <span className="fc-eyebrow">{t('compatBirthSummary')}</span>
              <span className="bz-recap__value fc-num">
                A {summaryDate(people.a)}
                <span className="bz-recap__sep">·</span>
                B {summaryDate(people.b)}
              </span>
            </div>
            <button type="button" className="fc-btn fc-btn--secondary fc-btn--sm" onClick={() => setResult(null)}>
              {t('compatEdit')}
            </button>
          </div>

          <section className="bz-block">
            <div className="fc-card compat-hero">
              <div>
                <span className="fc-eyebrow">{t('compatOverall')}</span>
                <h2>{txt(compatibility.grade)}</h2>
                <p>{txt(compatibility.verdict)}</p>
              </div>
              <div className="compat-score" aria-label={`${t('score')} ${compatibility.score}`}>
                <strong>{compatibility.score}</strong>
                <span>{t('score')}</span>
              </div>
            </div>
          </section>

          <section className="bz-block">
            <h2 className="bz-section-title">{t('compatChartsTitle')}</h2>
            <div className="bz-grid-2">
              {personCards.map(([key, person]) => (
                <article className="fc-card compat-person-card" key={key}>
                  <span className="fc-eyebrow">{t(personLabels[key])}</span>
                  <h3>{person.pillars?.year} · {person.pillars?.month} · {person.pillars?.day} · {person.pillars?.hour}</h3>
                  <div className="fc-defs">
                    <div className="fc-def"><span className="fc-def__key">{t('gender')}</span><span className="fc-def__val">{txt(person.gender)}</span></div>
                    <div className="fc-def"><span className="fc-def__key">{t('dayMaster')}</span><span className="fc-def__val">{txt(person.day_master)} {txt(person.day_master_element)}</span></div>
                    <div className="fc-def"><span className="fc-def__key">{t('zodiac')}</span><span className="fc-def__val">{txt(person.zodiac)}</span></div>
                    <div className="fc-def"><span className="fc-def__key">{t('strength')}</span><span className="fc-def__val">{txt(person.strength)}</span></div>
                    <div className="fc-def"><span className="fc-def__key">{t('usefulElements')}</span><span className="fc-def__val">{txt(person.useful_elements?.join('、')) || txt('流通中和')}</span></div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          {pairFortuneCards.length > 0 && (
            <section className="bz-block">
              <h2 className="bz-section-title">{t('compatPairFortunesTitle')}</h2>
              <p className="compat-pair-fortunes__desc">{t('compatPairFortunesDesc')}</p>
              <div className="bz-grid-2">
                {pairFortuneCards.map(([key, person]) => {
                  const luckStart = person.luck_start || {};
                  const currentAnnual = (person.annual_fortunes?.years || []).find((year) => year.position === 'current')
                    || person.annual_fortunes?.years?.[1]
                    || person.annual_fortunes?.years?.[0];
                  return (
                    <article className="fc-card compat-pair-fortunes" key={`pair-fortune-${key}`}>
                      <span className="fc-eyebrow">{t(personLabels[key])}</span>
                      <h3>{t('compatPairFortunesTitle')}</h3>
                      <div className="fc-defs">
                        <div className="fc-def">
                          <span className="fc-def__key">{t('compatLuckStart')}</span>
                          <span className="fc-def__val">
                            {luckStart.start_age ? `${luckStart.start_age}${t('age')}` : '—'} · {txt(luckStart.direction) || '—'}
                          </span>
                        </div>
                        <div className="fc-def">
                          <span className="fc-def__key">{t('compatAnnualPreview')}</span>
                          <span className="fc-def__val">
                            {currentAnnual ? `${currentAnnual.year} ${txt(currentAnnual.pillar)} · ${txt(currentAnnual.tone)} · ${currentAnnual.score}${t('score')}` : '—'}
                          </span>
                        </div>
                      </div>
                      <div className="compat-mini">
                        <span className="fc-eyebrow">{t('luckPillars')}</span>
                        <ul>
                          {(person.luck_pillars || []).slice(0, 3).map((pillar, index) => (
                            <li key={`${pillar.pillar}-${pillar.age_range}-${index}`}>
                              {pillar.age_range}{t('age')} · {txt(pillar.pillar)} · {txt(pillar.ten_god)}：{txt(pillar.plain)}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="compat-mini">
                        <span className="fc-eyebrow">{t('compatYearWindow')}</span>
                        <ul>
                          {(person.annual_fortunes?.years || []).map((year) => (
                            <li key={`${key}-${year.year}`}>
                              {year.year} {txt(year.pillar)} · {txt(year.tone)} · {year.score}{t('score')}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          <section className="bz-block">
            <h2 className="bz-section-title">{t('compatAspectTitle')}</h2>
            <div className="compat-aspect-grid">
              {compatibility.aspects?.map((aspect) => (
                <article className="fc-card compat-aspect-card" key={aspect.key}>
                  <div className="compat-aspect-card__head">
                    <h3>{txt(aspect.title)}</h3>
                    <span className="fc-tag fc-tag--accent">{txt(aspect.tone)} · {aspect.score}</span>
                  </div>
                  <p>{txt(aspect.summary)}</p>
                  <div className="compat-mini">
                    <span className="fc-eyebrow">{t('compatEvidence')}</span>
                    <ul>
                      {aspect.evidence?.map((item, index) => <li key={index}>{txt(item)}</li>)}
                    </ul>
                  </div>
                  <div className="compat-mini">
                    <span className="fc-eyebrow">{t('compatAdvice')}</span>
                    <ul>
                      {aspect.advice?.map((item, index) => <li key={index}>{txt(item)}</li>)}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="bz-block">
            <div className="fc-card compat-source">
              <span className="fc-eyebrow">{t('compatMethodTitle')}</span>
              <p>{txt(compatibility.source_basis?.book)}：{txt(compatibility.source_basis?.principle)}</p>
            </div>
          </section>

          <section className="bz-block">
            <h2 className="bz-section-title">{t('compatSignsTitle')}</h2>
            <div className="compat-sign-grid">
              {compatibility.relationship_signs?.highlights?.length ? compatibility.relationship_signs.highlights.map((item, index) => (
                <article className="compat-sign-card" key={`${item.pillar}-${index}`}>
                  <span className="fc-eyebrow">{txt(item.pillar)}</span>
                  <strong>{txt(item.a)} × {txt(item.b)} · {txt(item.label)}</strong>
                  <p>{txt(item.text)}</p>
                </article>
              )) : (
                <article className="compat-sign-card">
                  <span className="fc-eyebrow">{t('relationship')}</span>
                  <strong>{t('compatNoMajorSigns')}</strong>
                  <p>{t('compatNoMajorSignsCopy')}</p>
                </article>
              )}
            </div>
          </section>

          <section className="bz-block">
            <div className="fc-card">
              <h2 className="bz-section-title">{t('compatFinalAdvice')}</h2>
              <ul className="bz-modern-list">
                {compatibility.advice?.map((item, index) => <li key={index}>{txt(item)}</li>)}
              </ul>
            </div>
          </section>

          <Chatbot baziContext={result} t={t} lang={lang} />
        </section>
      )}
    </div>
  );
};

export default CompatibilityPage;
